// @givmo/fundraisers-module — the season setup's pure helpers (FCM-WEB34): the schedule as the organizer types
// it, each game's page and nightly fundraiser named by matchup and day (decision 8), the instants a game's
// first pitch and end are, the bodies of the console's three calls (FCM-BE119's documented order: the
// season's settings, its pledge template, then the games), and which typed games the act's answer refused.
// Every request shape is the generated client's; nothing here re-models a backend type.
import {
  firstErrorCode,
  isGivmoError,
  type P2pCampaignOut,
  type P2pConfigOut,
  type P2pLayerNodeOut,
  type P2pRecipientListIn,
  type P2pSeasonGameIn,
  type P2pSeasonSetupOut,
  type P2pSeasonSetupRequest,
  type PledgeOfferCreate,
  type PledgeRuleSentencePreviewOut,
  type PledgeTemplateMeasureWrite,
  type PledgeTemplateOffer,
  type PledgeTemplateOut,
  type PledgeTemplateWrite,
} from '@givmo/api-client';
import { zonedInstant } from './emails-decode';
import { endOfDay, layersLive } from './layers-decode';
import { SEASON_COPY as S } from './season-setup-copy';

/**
 * Whether the season setup serves here: FCM-BE117's `featured_fundraiser_id` rides the layers and the
 * pledge engine together (dark == missing), exactly the switches the act, the template's routes and a
 * game's featured fundraiser are behind.
 */
export function seasonLive(config: P2pConfigOut | null | undefined): boolean {
  return layersLive(config) && config?.featured_fundraiser_id !== undefined;
}

/** One scheduled game as typed: the date input's "2027-04-03", the time input's "13:05", the opponent. */
export interface GameRow {
  key: number;
  day: string;
  time: string;
  opponent: string;
}

export type RatePolicy = 'suggested' | 'fixed' | 'pledgor_choice';

/** The scoreboard and the nightly pledge, as typed: the season's pledge template, both halves. */
export interface SeasonTerms {
  subject: string;
  unit: string;
  most: string;
  rule: string;
  phrase: string;
  ratePolicy: RatePolicy;
  rate: string;
  cap: string;
}

export const EMPTY_TERMS: SeasonTerms = {
  subject: '',
  unit: '',
  most: '',
  rule: '',
  phrase: '',
  ratePolicy: 'suggested',
  rate: '',
  cap: '',
};

export const complete = (game: GameRow) =>
  game.day !== '' && game.time !== '' && game.opponent.trim() !== '';
export const blank = (game: GameRow) =>
  game.day === '' && game.time === '' && game.opponent.trim() === '';

/** "2027-04-03" as that day, in the reader's zone. */
export function dayOf(day: string): Date {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  return new Date(year, month - 1, date);
}

const SHORT_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

/** A game's day as its page's name carries it, and as the remove toast names it: "Apr 3". */
export function shortDay(day: string): string {
  return SHORT_DAY.format(dayOf(day));
}

/**
 * A text as shown, with a day ("Apr 10") kept on one line: the space between a month and its day becomes a
 * no-break space (tester-5's item 12). For display only: a name the act writes keeps its own spaces.
 */
export function keepDayTogether(text: string): string {
  return text.replace(
    /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2})\b/g,
    '$1\u00a0$2',
  );
}

/** "13:05" on `day`, as an instant: in `zone` when one is named, else in the reader's zone. */
export function firstPitch(day: string, time: string, zone?: string): string {
  if (zone !== undefined) {
    const instant = zonedInstant({ date: day, time }, zone);
    if (instant !== null) return instant;
  }
  const [hours, minutes] = time.split(':').map(Number) as [number, number];
  const date = dayOf(day);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
}

/** Decision 8: the game's page, "Herons vs Eagles, Apr 3", and its nightly fundraiser's name. */
export function gameNames(subject: string, game: GameRow): { name: string; nightly: string } {
  const matchup = S.vsName(subject, game.opponent.trim());
  const day = shortDay(game.day);
  return { name: S.gamePageName(matchup, day), nightly: S.nightlyName(matchup, day) };
}

/** Each typed game as the act takes it: its pages' names, its first pitch, its own end at 11:59 PM on its
 * day (the ruled end, its deadline and its pledge's), the zone they are read in, and its opponent's side. */
export function gamesBody(
  games: readonly GameRow[],
  subject: string,
  zone: string,
): P2pSeasonGameIn[] {
  return games.map((game) => {
    const names = gameNames(subject, game);
    return {
      name: names.name,
      nightly_name: names.nightly,
      starts_at: firstPitch(game.day, game.time, zone),
      ends_at: endOfDay(dayOf(game.day), zone),
      time_zone: zone,
      opponent: { subject_name: game.opponent.trim() },
    };
  });
}

/** The last typed game's day, which the season's deadline follows until the organizer sets one. */
export function lastDay(games: readonly GameRow[]): Date | undefined {
  return games.filter(complete).reduce<Date | undefined>((latest, game) => {
    const day = dayOf(game.day);
    return latest === undefined || day > latest ? day : latest;
  }, undefined);
}

/**
 * Whole cents for a typed dollar amount ("1", "1.5", "25.00"), read as text so no float rounds it; null for
 * anything else, a zero among them.
 */
export function centsOf(text: string): number | null {
  const match = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(text.trim());
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return cents > 0 ? cents : null;
}

/** A stored amount in cents as the field shows it again ("1.00"). */
export function dollarsOf(cents: number | null | undefined): string {
  if (cents == null || !Number.isSafeInteger(cents) || cents <= 0) return '';
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

/** The unit's plural as the setup's lines say it ("runs"), until the server's preview names it. */
export function unitsOf(unit: string): string {
  const trimmed = unit.trim();
  return trimmed ? `${trimmed}s` : '';
}

const blankToNull = (value: string) => (value.trim() === '' ? null : value.trim());

/** The season's count (the scoreboard), as the template stores it: whom it counts, what, and its words. */
function seasonMeasure(terms: SeasonTerms): PledgeTemplateMeasureWrite {
  return {
    kind: 'count',
    unit_label: terms.unit.trim(),
    count_precision: 0,
    subject_name: blankToNull(terms.subject),
    qualifying_rule: blankToNull(terms.rule),
    declared_max_units: Number(terms.most) > 0 ? Number(terms.most) : null,
    starts_at: null,
    ends_at: null,
    timezone: null,
    live_mode: true,
    health_data: false,
    count_phrase: blankToNull(terms.phrase),
  };
}

/** The nightly pledge's terms: per unit, the rate as set, closing at the final out (D-GFU-6). It names no
 * opening (D-GFU-57: pledging opens when each game's pledge is published). */
function seasonOffer(terms: SeasonTerms): PledgeTemplateOffer {
  return {
    amount_rule: 'per_unit',
    rate_policy: terms.ratePolicy,
    suggested_rate_cents: terms.ratePolicy === 'pledgor_choice' ? null : centsOf(terms.rate),
    default_cap_cents: centsOf(terms.cap) ?? 0,
    tone: 'celebratory',
    window: 'at_end',
  };
}

/** The fields the terms still lack, in the page's order: whom it counts, what, the most, the rate where
 * the organizer sets one, and the cap. */
export function missingTerms(terms: SeasonTerms): (keyof SeasonTerms)[] {
  const missing: (keyof SeasonTerms)[] = [];
  if (terms.subject.trim() === '') missing.push('subject');
  if (terms.unit.trim() === '') missing.push('unit');
  if (!(Number(terms.most) > 0)) missing.push('most');
  if (terms.ratePolicy !== 'pledgor_choice' && centsOf(terms.rate) === null) missing.push('rate');
  if (centsOf(terms.cap) === null) missing.push('cap');
  return missing;
}

/** Whether the terms say enough to store and preview. */
export function termsReady(terms: SeasonTerms): boolean {
  return missingTerms(terms).length === 0;
}

/**
 * A stored template's terms as the setup's fields hold them, so the page opens on what the season
 * already carries; null for one the setup can't show (anything but a count per unit it reports).
 */
export function termsOfTemplate(
  template: PledgeTemplateOut | null | undefined,
): SeasonTerms | null {
  if (!template) return null;
  const { measure, offer } = template;
  if (measure.kind !== 'count' || measure.rollup || offer.amount_rule !== 'per_unit') return null;
  const policy: RatePolicy =
    offer.rate_policy === 'fixed' || offer.rate_policy === 'pledgor_choice'
      ? offer.rate_policy
      : 'suggested';
  return {
    subject: measure.subject_name ?? '',
    unit: measure.unit_label,
    most: measure.declared_max_units != null ? String(measure.declared_max_units) : '',
    rule: measure.qualifying_rule ?? '',
    phrase: measure.count_phrase ?? '',
    ratePolicy: policy,
    rate: policy === 'pledgor_choice' ? '' : dollarsOf(offer.suggested_rate_cents),
    cap: dollarsOf(offer.default_cap_cents),
  };
}

/** The season's pledge template (`PUT /fundraisers/{id}/pledge-template`): the second of the three calls. */
export function templateBody(terms: SeasonTerms): PledgeTemplateWrite {
  return { measure: seasonMeasure(terms), offer: seasonOffer(terms) };
}

/**
 * The server's own sentence for these terms (`POST /pledge-offers/rule-sentence`, which writes nothing):
 * the template's offer over a new count on the season itself, dated like one game (the first typed, else
 * the day after today), since only an Official Source event's name changes the sentence.
 */
export function previewBody(
  seasonId: string,
  terms: SeasonTerms,
  game: GameRow | undefined,
  zone: string,
  now: Date = new Date(),
): PledgeOfferCreate {
  const day = game
    ? game.day
    : (() => {
        const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
      })();
  const measure = seasonMeasure(terms);
  return {
    fundraiser_id: seasonId,
    measure: {
      kind: 'count',
      unit_label: measure.unit_label,
      count_precision: 0,
      subject_name: terms.subject.trim(),
      subject_affiliation: 'official',
      qualifying_rule: measure.qualifying_rule,
      declared_max_units: measure.declared_max_units,
      starts_at: firstPitch(day, game ? game.time : '12:00', zone),
      ends_at: endOfDay(dayOf(day), zone),
      timezone: zone,
      live_mode: true,
      health_data: false,
      count_phrase: measure.count_phrase,
    },
    amount_rule: 'per_unit',
    rate_policy: terms.ratePolicy,
    suggested_rate_cents: seasonOffer(terms).suggested_rate_cents,
    default_cap_cents: centsOf(terms.cap) ?? 0,
    tone: 'celebratory',
  };
}

/** The unit's plural a stored template counts, for "Add games", whose terms are the season's as stored. */
export function storedUnits(template: PledgeTemplateOut | null | undefined): string {
  return template ? unitsOf(template.measure.unit_label) : '';
}

/**
 * "Pledgors will see", as the server's preview words it: its sentence (at the rate as set, else the
 * sentence a pledgor's own rate reads), its "What counts" part on a line of its own, and its precision
 * line. The parts are the server's own words, never reworded; null until it has a sentence.
 */
export function previewLines(
  preview: PledgeRuleSentencePreviewOut | null | undefined,
): { sentence: string; whatCounts: string | null; precision: string | null } | null {
  const sentence = preview?.rule_sentence || preview?.rule_sentence_rate_free || null;
  if (!sentence) return null;
  const at = sentence.indexOf(' What counts: ');
  return {
    sentence: at < 0 ? sentence : sentence.slice(0, at),
    whatCounts: at < 0 ? null : sentence.slice(at + 1),
    precision: preview?.precision_line || null,
  };
}

/** The current Terms of Use as the setup shows them (D-FCM-65): their id, version and plain text. */
export interface CurrentTerms {
  id: string;
  version: number;
  text: string;
}

/** `GET /terms/terms_of_service/latest`'s answer, read tolerantly; null for anything else. */
export function currentTerms(body: unknown): CurrentTerms | null {
  if (body === null || typeof body !== 'object') return null;
  const { id, version_number: version, content_plaintext: text } = body as Record<string, unknown>;
  return typeof id === 'string' &&
    id !== '' &&
    typeof version === 'number' &&
    Number.isSafeInteger(version) &&
    typeof text === 'string'
    ? { id, version, text }
    : null;
}

/** The act's body (FCM-BE119): the games, the Terms the page showed (every game names an opponent, a
 * count its organizer agrees to the Terms for), and under a season whose supporters pick, the nightly
 * fundraisers' picks; a season that picks its own sends none. */
export function seasonBody(
  games: readonly GameRow[],
  subject: string,
  zone: string,
  termsId: string,
  picks: P2pRecipientListIn | null,
): P2pSeasonSetupRequest {
  return {
    games: gamesBody(games, subject, zone),
    creator_terms_document_id: termsId,
    ...(picks ? { nightly_recipients: picks } : {}),
  };
}

/** The Terms refusals (D-FCM-65): the Terms changed since the page read them, or none are current. */
export const TERMS_REFUSALS: ReadonlySet<string> = new Set([
  'PLEDGE_CREATOR_TERMS_CHANGED',
  'PLEDGE_CREATOR_TERMS_UNAVAILABLE',
]);

/**
 * Whether the act may be asked again with the same key and body: no answer came (the connection or a
 * gateway lost it), or the act is still running (409 `IDEMPOTENCY_IN_PROGRESS`), whose answer the same
 * key replays once it is done.
 */
export function askAgain(error: unknown): boolean {
  if (!isGivmoError(error)) return false;
  if (error.isTransient) return true;
  if (error.status === 502 || error.status === 503 || error.status === 504) return true;
  return error.status === 409 && firstErrorCode(error) === 'IDEMPOTENCY_IN_PROGRESS';
}

/**
 * Which typed games the act did not make, each with the refusing writer's own sentence (null: a fault, which
 * has none), keyed by the row it came from: the answer names each by its place in the request.
 */
export function refusedRows(
  asked: readonly GameRow[],
  answer: P2pSeasonSetupOut,
): Map<number, string | null> {
  const refused = new Map<number, string | null>();
  for (const game of answer.refused) {
    const row = asked[game.index];
    if (row) refused.set(row.key, game.detail ?? null);
  }
  return refused;
}

/**
 * D-FCM-56's line under a game's tree: what its page shows first (the public read's
 * `pledges.featured_fundraiser_id`, FCM-BE121's rule applied), said as the game's choice where it made
 * one (the settings read's echo) and as the default rule's pick where it made none; null where its page
 * features none. A stored choice its page can't feature shows what the page does (review-82's P3).
 */
export function featuredLineOf(
  echo: string | null | undefined,
  shown: string | null | undefined,
  nameOf: (id: string) => string,
): string | null {
  // Only what the page shows first (review-88's F5): a stored choice the page can't feature, with nothing
  // featured in its place, says nothing first. The echo only says how to word what is shown.
  if (!shown) return null;
  return echo ? S.featuredLine(nameOf(shown)) : S.featuredDefaultLine(nameOf(shown));
}

/** What a campaign's page shows first, per its public read; null while it shows none (or serves none). */
export function shownFirst(campaign: P2pCampaignOut | null | undefined): string | null {
  return campaign?.pledges?.featured_fundraiser_id ?? null;
}

/**
 * The fundraisers a game may feature (D-FCM-56; FCM-BE117's rule): a published campaign directly under
 * it, open or closed, that carries a pledge on its page, and whichever it features now.
 */
export function featuredCandidates(
  gameId: string,
  children: readonly P2pLayerNodeOut[],
  game: P2pCampaignOut | null | undefined,
  keep: readonly (string | null | undefined)[],
): P2pLayerNodeOut[] {
  const hosts = new Set((game?.pledges?.offers ?? []).map((offer) => offer.fundraiser_id));
  return children.filter(
    (node) =>
      node.parent_campaign_id === gameId &&
      (node.state === 'active' || node.state === 'closed') &&
      (hosts.has(node.campaign_id) || keep.includes(node.campaign_id)),
  );
}
